import { ReactNode, createContext, useContext } from 'react'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { ONBOARDING_SEQUENCE, ONBOARDING_SEQUENCE_ROUTES } from '../routes/public'
import { TenantOnboardingStage } from '$api-client/types.gen'

export type OnboardingContextType = {
  stage: TenantOnboardingStage,
  onboardedAt: string | null,
  isLoading: boolean,
  getNextStage: (currentStage: TenantOnboardingStage) => TenantOnboardingStage,
  hasCompletedOnboarding: boolean,
  getNextStageRoute: () => string,
  getCurrentStageRoute: () => string,
}

export const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export type OnboardingProviderProps = {
  children: ReactNode
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { stage, onboardedAt, isLoading: isTenantLoading } = useTenant()

  function getNextStage(currentStage: TenantOnboardingStage): TenantOnboardingStage {
    const currentIndex = ONBOARDING_SEQUENCE.indexOf(currentStage)

    if (currentIndex === -1) {
      return ONBOARDING_SEQUENCE[1]
    }

    return ONBOARDING_SEQUENCE[currentIndex + 1]
  }

  function getNextStageRoute(): string {
    return ONBOARDING_SEQUENCE_ROUTES[getNextStage(stage!)]
  }

  function getCurrentStageRoute(): string {
    return ONBOARDING_SEQUENCE_ROUTES[stage!]
  }

  const context: OnboardingContextType = {
    stage: stage!,
    onboardedAt: onboardedAt!,
    isLoading: isTenantLoading,
    hasCompletedOnboarding: stage === TenantOnboardingStage.FINISHED && !!onboardedAt,
    getNextStage,
    getNextStageRoute,
    getCurrentStageRoute,
  }

  return (
    <OnboardingContext.Provider value={context}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)

  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }

  return context
}
